import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
 ArrowLeft, CheckCircle, Circle, ChevronRight, ChevronLeft,
 AlertCircle, Sparkles, BookOpen, FileText, Clock, Image, Headphones,
 Video, ExternalLink, Download, Award,
} from 'lucide-react';
import AdBanner from '../components/AdBanner';
import HeadlineMetric from '../components/HeadlineMetric';
import PageHero from '../components/PageHero';
import ToolSelector from '../components/ToolSelector';
import LessonTour from '../components/LessonTour';
import { TourLaunchButton } from '../components/PageTour';
import { ChecklistSection, PollSection, AssignmentPanel, DiscussionPanel } from '../components/LessonEngagement';
import { ProgressBar } from '../components/lms/CompletionBadges';
import CourseCompletionCelebration from '../components/CourseCompletionCelebration';
import ModuleCompleteModal from '../components/ModuleCompleteModal';
import ConfidenceSurvey from '../components/ConfidenceSurvey';
import AdSlot from '../components/AdSlot';
import LessonStudyPanel from '../components/LessonStudyPanel';
import LessonCitationsPanel, { transformCiteMarkers } from '../components/LessonCitationsPanel';
import LessonAudiobookMode from '../components/LessonAudiobookMode';
import useLessonTOC from '../hooks/useLessonTOC';
import useTOCScrollProgress from '../hooks/useTOCScrollProgress';
import { AutoTranslated, AutoTranslatedHtml } from '../components/i18n/AutoTranslated';
import * as richTextLib from '../lib/richText';
// Defensive namespace import — a prior fork occasionally hit a
// `mdToHtml is not defined` runtime error when a stale bundle stripped
// the named export. Keeping a passthrough fallback ensures the lesson
// still renders (raw markdown, but visible) even if the helper is
// somehow missing at load time.
const mdToHtml = richTextLib.mdToHtml || ((s) => String(s || ''));
import LessonExtraModals from '../components/lesson/LessonExtraModals';
import UnitConverterPopover from '../components/lesson/UnitConverterPopover';
import PageSEO, { buildLessonSchema } from '../components/PageSEO';
import RelatedOnTopic from '../components/RelatedOnTopic';
import MilestoneToast from '../components/lms/MilestoneToast';
import AskExpertButton from '../components/AskExpertButton';
import ProtectedContent from '../components/ProtectedContent';
import RefundWindowBanner from '../components/RefundWindowBanner';
import VideoWatermarkWrap from '../components/VideoWatermarkWrap';
import WatermarkedPdfViewer from '../components/WatermarkedPdfViewer';
import WatermarkedImage from '../components/WatermarkedImage';
import useWatermarkHtmlImages from '../hooks/useWatermarkHtmlImages';

const LessonViewer = () => {
 const { lessonId } = useParams();
 const navigate = useNavigate();
 const { API, user } = useContext(AuthContext);
 const [lesson, setLesson] = useState(null);
 const [loading, setLoading] = useState(true);
 const [completing, setCompleting] = useState(false);
 const [showQuiz, setShowQuiz] = useState(false);
 const [tourOpen, setTourOpen] = useState(false);
 const [quizAnswers, setQuizAnswers] = useState({});
 const [quizResult, setQuizResult] = useState(null);
 // Course-wide progress meter — rolls up every lesson completion in the
 // course (checklist + quiz already gate lesson completion, so this
 // single meter reflects all three exercise types).
 const [courseProgress, setCourseProgress] = useState(null);
 const [showCompletionModal, setShowCompletionModal] = useState(false);
 const [verifiedSeconds, setVerifiedSeconds] = useState(0);
 const [certificate, setCertificate] = useState(null); // { cert_number, course_title } — set when a fresh course cert is minted
 const [moduleCompletion, setModuleCompletion] = useState(null); // { module_title, course_title } — set when a module was just fully cleared
 // Confidence-survey modal state. `trigger` decides which copy to render;
 // context carries lesson/course/quiz IDs so the backend can log them.
 const [confidenceState, setConfidenceState] = useState({ open: false, trigger: 'lesson_complete' });
 const heartbeatRef = useRef(null);
 const lastCoursePctRef = useRef(null); // remembers course% between lesson toggles
 const [milestone, setMilestone] = useState(null); // one of 25 | 50 | 75 | null
 // Lesson citations + audiobook mode — populated once we have the lesson body.
 const [lessonCitations, setLessonCitations] = useState([]);
 const [audiobookOpen, setAudiobookOpen] = useState(false);
 // Lesson-tools modals: Study Together (peer study room), Requirements
 // (prereqs / equipment / access code). Instructor id is captured from the
 // /study-context payload so the "Instructor & office hours" button can
 // deep-link to the dedicated instructor profile page.
 const [studyModalOpen, setStudyModalOpen] = useState(false);
 const [requirementsOpen, setRequirementsOpen] = useState(false);
 const [instructorId, setInstructorId] = useState(null);
 // Prereq auto-unlock banner state — populated when the learner arrived
 // at THIS lesson via the "Lesson requirements → prereq" link on
 // another lesson. Once this lesson is marked complete we auto-jump
 // back to the original lesson so the flow closes cleanly.
 const [returnCtx, setReturnCtx] = useState(null);
 // Content protection state — refund window, daily drip counters, watermark.
 // Fetched once per lesson so the banner + watermark reflect current values.
 const [protectionState, setProtectionState] = useState(null);
 // Ref used by `useWatermarkHtmlImages` to intercept every <img> rendered
 // inside lesson rich-text HTML and swap its src for a watermarked blob.
 const contentRef = useRef(null);
 useWatermarkHtmlImages(contentRef, { lessonId, courseId: lesson?.course_id });

 // Extract every citeable text field from the lesson so we can (a) feed the
 // whole thing to the audiobook TTS and (b) transform inline `[cite:doc-id]`

 // Parse the lesson body HTML for `<h2>` anchors so the right-rail
 // sidebar can render a jump-TOC and body headings pick up ids.
 // (Note: declared AFTER `contentRef` — the TOC progress hook needs
 // to read the ref, so `useLessonTOC` + `useTOCScrollProgress` must
 // both sit below the `useRef` call to avoid a TDZ crash.)
 const lessonBody = useLessonTOC(lesson?.description || '');
 // Track which sections the learner has scrolled past so the sidebar
 // TOC can turn each number green as they progress through the lesson.
 const sectionsRead = useTOCScrollProgress(contentRef, lessonBody.toc);
 // markers to numbered `[N]` references consistent with the citations panel.
 const buildLessonPlainText = (l) => {
   if (!l) return '';
   const stripHtml = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
   const parts = [];
   if (l.title) parts.push(l.title);
   // Lesson body — the AI-generated study content lives on `l.description`
   // as HTML (from the backend markdown pipeline). Historically this was
   // omitted so audiobook mode only read the Key Takeaways / section
   // content. Push it FIRST so the narrator reads the actual body before
   // the takeaways / quiz appended by content_sections below.
   if (l.description) {
     const body = stripHtml(l.description);
     if (body) parts.push(body);
   }
   if (l.written_content) {
     const stripped = stripHtml(l.written_content);
     if (stripped) parts.push(stripped);
   }
   if (l.markdown_content) parts.push(l.markdown_content);
   for (const s of (l.content_sections || []).sort((a, b) => (a.order || 0) - (b.order || 0))) {
     // Skip image / diagram sections entirely — their markdown_content
     // used to embed a 1 MB base64 data URL that blew past the TTS
     // char cap and made audiobook generation fail with a 413 every time.
     if (s.type === 'image') continue;
     for (const k of ['content', 'html', 'markdown', 'text', 'markdown_content', 'written_content']) {
       if (s[k]) {
         const val = k.includes('html') || k === 'written_content'
           ? stripHtml(s[k])
           : String(s[k]);
         if (val) parts.push(val);
       }
     }
   }
   return parts.join('\n\n');
 };

 // Anti-cheat heartbeat — pings server every 15s while lesson is open.
 useEffect(() => {
   if (!lessonId || !API) return undefined;
   const tick = async () => {
     try {
       const token = window.localStorage.getItem('token');
       const res = await axios.post(`${API}/lms/lessons/${lessonId}/heartbeat`, {}, {
         headers: token ? { Authorization: `Bearer ${token}` } : {},
       });
       if (res.data?.verified_time_seconds != null) setVerifiedSeconds(res.data.verified_time_seconds);
     } catch (_) { /* silent */ }
   };
   tick();
   heartbeatRef.current = setInterval(tick, 15000);
   return () => heartbeatRef.current && clearInterval(heartbeatRef.current);
 }, [lessonId, API]);

 useEffect(() => {
 fetchLesson();
 }, [lessonId, API]);

 // Seed the course-progress cache so we can detect crossings (25/50/75)
 // on the very next lesson completion instead of the second one.
 // Also feeds the sticky Course Progress meter at the top of the lesson.
 useEffect(() => {
 if (!lesson?.course_id || !API) return undefined;
 let cancelled = false;
 const refresh = () => axios.get(`${API}/lms/my-progress`)
 .then((res) => {
 if (cancelled) return;
 const row = (res.data?.progress || []).find((c) => c.course_id === lesson.course_id);
 lastCoursePctRef.current = Math.round(row?.progress_percentage || 0);
 setCourseProgress(row || null);
 })
 .catch(() => { /* silent */ });
 refresh();
 const onProgressUpdated = () => refresh();
 window.addEventListener('lms:progress-updated', onProgressUpdated);
 return () => {
 cancelled = true;
 window.removeEventListener('lms:progress-updated', onProgressUpdated);
 };
 }, [lesson?.course_id, API]);

 // Keyboard navigation
 useEffect(() => {
 const handleKeyPress = (e) => {
 // Don't trigger if user is typing in an input/textarea
 if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
 
 if (e.key === 'ArrowLeft' && lesson?.prev_lesson) {
 navigate(`/lesson/${lesson.prev_lesson.id}`);
 } else if (e.key === 'ArrowRight' && lesson?.next_lesson) {
 navigate(`/lesson/${lesson.next_lesson.id}`);
 }
 };

 window.addEventListener('keydown', handleKeyPress);
 return () => window.removeEventListener('keydown', handleKeyPress);
 }, [lesson, navigate]);

 const fetchLesson = async () => {
 try {
 setLoading(true);
 const response = await axios.get(`${API}/lms/lessons/${lessonId}`);
 setLesson(response.data);
 // Fire-and-forget: hydrate the study context so we know which
 // instructor to deep-link from the "Instructor & office hours"
 // button in the Lesson Tools sidebar. A missing instructor is
 // silently ignored — the button surfaces a toast in that case.
 try {
   const ctxRes = await axios.get(`${API}/lms/lessons/${lessonId}/study-context`);
   if (ctxRes.data?.instructor?.id) setInstructorId(ctxRes.data.instructor.id);
 } catch (_) { /* silent */ }
 // Pick up any pending "return to lesson X after finishing this prereq"
 // hint so we can surface a banner AND auto-navigate on completion.
 try {
   const raw = localStorage.getItem('lms:requirements-return');
   if (raw) {
     const parsed = JSON.parse(raw);
     // Only surface the banner if we're actually on the prereq lesson
     // (or if the saved entry doesn't identify a specific prereq).
     if (
       parsed?.return_to_lesson_id
       && parsed.return_to_lesson_id !== lessonId
       && (!parsed.prereq_lesson_id || parsed.prereq_lesson_id === lessonId)
     ) {
       setReturnCtx(parsed);
     }
   }
 } catch { /* ignore parse errors */ }
 // Content-protection unlock + snapshot — kicks the drip counter in the
 // 14-day refund window and returns the diagonal watermark payload.
 // 429 means the user has hit the daily unlock limit; we still render
 // the lesson (they may be revisiting), but we surface the banner.
 try {
   const unlockRes = await axios.post(
     `${API}/content-protection/unlock-lesson`,
     { lesson_id: lessonId, course_id: response.data?.course_id },
   );
   setProtectionState(unlockRes.data);
 } catch (unlockErr) {
   if (unlockErr.response?.status === 429) {
     toast.warning(unlockErr.response?.data?.detail || 'Daily lesson limit reached.');
     try {
       const stat = await axios.get(`${API}/content-protection/status`);
       setProtectionState(stat.data);
     } catch (_) { /* silent */ }
   }
 }
 } catch (error) {
 console.error('Error fetching lesson:', error);
 toast.error('Failed to load lesson');
 } finally {
 setLoading(false);
 }
 };

 const toggleComplete = async () => {
 try {
 setCompleting(true);
 const newCompletedState = !lesson.completed;

 // Enforce checklist completion when a section has require_all=true.
 // Reads the same localStorage the ChecklistSection uses so we don't
 // have to plumb React state across siblings; the checklist widget
 // reconciles its state with the server on mount and mirrors it into
 // localStorage on every toggle, so this is always up-to-date after
 // the initial paint.
 if (newCompletedState) {
   const blocking = (lesson.content_sections || []).find(sec => {
     if (sec?.type !== 'checklist') return false;
     if (!sec?.checklist_data?.require_all) return false;
     const items = sec.checklist_data?.items || [];
     if (!items.length) return false;
     let checkedIds = [];
     try {
       checkedIds = JSON.parse(localStorage.getItem(`lms-checklist:${lessonId}:${sec.id}`) || '[]');
     } catch { checkedIds = []; }
     const checkedSet = new Set(checkedIds);
     return items.some(i => !checkedSet.has(i.id));
   });
   if (blocking) {
     toast.warning('Finish the checklist "' + (blocking.title || 'recap') + '" first — every item is required.');
     setCompleting(false);
     return;
   }
 }

 await axios.post(`${API}/lms/progress`, {
 lesson_id: lessonId,
 completed: newCompletedState,
 time_spent_minutes: lesson.duration_minutes || 0
 });

 setLesson(prev => ({ ...prev, completed: newCompletedState }));
 toast.success(newCompletedState ? 'Lesson marked as complete!' : 'Lesson marked as incomplete');

 // Notify any mounted course/catalog view that progress just changed.
 // CourseDetail and LearningCatalog listen and refetch — that's how
 // "already-completed" pills and progress bars stay in sync without
 // requiring a manual page reload after finishing a lesson.
 try {
   window.dispatchEvent(new CustomEvent('lms:progress-updated', {
     detail: {
       lesson_id: lessonId,
       module_id: lesson.module_id,
       course_id: lesson.course_id,
       completed: newCompletedState,
     },
   }));
 } catch {}

 // After the user just marked this lesson complete, ask them how confident
 // they feel. Deferred slightly so it doesn't stomp on the completion toast.
 if (newCompletedState) {
   setTimeout(() => {
     setConfidenceState({ open: true, trigger: 'lesson_complete' });
   }, 600);
 }

// Module-complete celebration — fires when the user just cleared the
// final remaining lesson in the module.
if (newCompletedState && lesson.module_id) {
try {
const modRes = await axios.get(`${API}/lms/modules/${lesson.module_id}/completion`);
if (modRes.data?.completed) {
setModuleCompletion({
module_id: lesson.module_id,
module_title: modRes.data.module_title,
course_title: modRes.data.course_title,
});
// Fetch the current streak so the shared image can flex it — best-effort.
try {
const streakRes = await axios.get(`${API}/lms/streak`);
setModuleCompletion((prev) => prev ? { ...prev, streak: streakRes.data?.current || 0 } : prev);
} catch { /* silent */ }
}
} catch { /* silent */ }
}


 // Award/refresh course completion badge whenever the user marks a lesson complete
 if (newCompletedState && lesson.course_id) {
 // Detect course-level milestone crossings (25/50/75) for the celebratory
 // toast. Fires once per threshold as the user climbs.
 try {
 const progRes = await axios.get(`${API}/lms/my-progress`);
 const row = (progRes.data?.progress || []).find((c) => c.course_id === lesson.course_id);
 const nowPct = Math.round(row?.progress_percentage || 0);
 const prevPct = lastCoursePctRef.current ?? Math.max(0, nowPct - 1);
 const crossed = [25, 50, 75].find((th) => prevPct < th && nowPct >= th && nowPct < 100);
 if (crossed) setMilestone(crossed);
 lastCoursePctRef.current = nowPct;
 } catch { /* silent */ }

 try {
 const badgeRes = await axios.get(`${API}/lms/courses/${lesson.course_id}/badge`);
 if (badgeRes.data?.earned && badgeRes.data?.earned_at) {
 const secondsAgo = (Date.now() - new Date(badgeRes.data.earned_at).getTime()) / 1000;
 if (secondsAgo < 30) {
 toast.success(`🏆 Course complete — badge unlocked: ${badgeRes.data.course_title}`);
 // Mint the certificate (idempotent) and open the celebration modal.
 try {
   const token = window.localStorage.getItem('token');
   const certRes = await axios.post(
     `${API}/lms/courses/${lesson.course_id}/certificate`,
     {},
     { headers: token ? { Authorization: `Bearer ${token}` } : {} },
   );
   setCertificate({
     cert_number: certRes.data.cert_number,
     course_title: certRes.data.course_title,
     holder_name: certRes.data.holder_name,
   });
 } catch (certErr) {
   // 403 = anti-cheat gate not cleared; the badge endpoint is more
   // permissive than the cert endpoint (quiz-required lessons).
   const detail = certErr.response?.data?.detail;
   if (detail) toast.info(detail);
 }
 }
 }
 } catch { /* silent */ }
 }
 
 // Show completion modal if marking as complete and there's a next lesson
 if (newCompletedState && lesson.next_lesson) {
 setShowCompletionModal(true);
 }
 } catch (error) {
 console.error('Error updating progress:', error);
 const detail = error.response?.data?.detail;
 if (error.response?.status === 403) {
   toast.error(detail || 'Anti-cheat: complete the lesson content first');
 } else {
   toast.error(detail || 'Failed to update progress');
 }
 } finally {
 setCompleting(false);
 }
 };

 const submitQuiz = async () => {
 if (!lesson.quiz) return;

 try {
 const response = await axios.post(`${API}/lms/quizzes/submit`, {
 quiz_id: lesson.quiz.id,
 answers: quizAnswers
 });

 setQuizResult(response.data);
 
 if (response.data.passed) {
 toast.success(`Quiz passed! Score: ${response.data.score}%`);
 // Auto-mark lesson complete if quiz passed
 if (!lesson.completed) {
 await toggleComplete();
 }
 } else {
 toast.error(`Quiz failed. Score: ${response.data.score}%. Keep trying!`);
 // Kick the confidence survey so the student can flag they need help.
 setTimeout(() => {
   setConfidenceState({ open: true, trigger: 'quiz_failed' });
 }, 600);
 }
 } catch (error) {
 console.error('Error submitting quiz:', error);
 toast.error('Failed to submit quiz');
 }
 };

 const getYouTubeId = (url) => {
 if (!url) return null;
 const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
 return match ? match[1] : null;
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center">
 <p className="text-slate-600 dark:text-neutral-400">Loading lesson...</p>
 </div>
 );
 }

 if (!lesson) {
 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800 flex items-center justify-center">
 <Card className="p-8 text-center">
 <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
 <h3 className="text-xl font-semibold text-slate-700 dark:text-neutral-300 mb-2">Lesson not found</h3>
 <Button onClick={() => navigate('/learning')}>Back to Courses</Button>
 </Card>
 </div>
 );
 }

 const videoId = getYouTubeId(lesson.video_url);

 const lessonWords = (lesson.title || '').trim().split(' ');
 const lessonLast = lessonWords.pop();
 const lessonRest = lessonWords.join(' ');
 const lessonHeroTitle = lessonWords.length
 ? <>{lessonRest} <span className="text-amber-brand">{lessonLast}</span></>
 : <span className="text-amber-brand">{lessonLast || lesson.title}</span>;

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <ToolSelector />
 <PageHero
 eyebrow={lesson.course_title || 'Lesson'}
 eyebrowIcon={BookOpen}
 title={lessonHeroTitle}
 description={lesson.module_title ? `Module: ${lesson.module_title}` : undefined}
 backTo={lesson.course_id ? `/course/${lesson.course_id}` : '/learning'}
 backLabel="Course"
 testId="lesson-hero"
 />
 <LessonTour forceOpen={tourOpen} onFinish={() => setTourOpen(false)} />
 <div className="container mx-auto px-4 py-8 max-w-7xl">
 <div className="flex flex-col lg:flex-row lg:items-start lg:gap-8" data-testid="lesson-layout">
 <div className="min-w-0 flex-1" data-testid="lesson-main-col">
 <div className="flex items-center justify-end mb-3">
 <TourLaunchButton
 onClick={() => setTourOpen(true)}
 label="Take the lesson tour"
 testId="lesson-tour-launch"
 />
 </div>
 {/* Progress bar */}
 {lesson.lesson_number && lesson.total_lessons_in_module && (
 <div className="mb-6 flex items-center gap-3" data-testid="lesson-progress">
 <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-bold uppercase tracking-widest">
 Lesson {lesson.lesson_number} of {lesson.total_lessons_in_module}
 </div>
 <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
 <div
 className="h-full bg-primary transition-all duration-300"
 style={{ width: `${(lesson.lesson_number / lesson.total_lessons_in_module) * 100}%` }}
 />
 </div>
 </div>
 )}

 {/* Course-wide progress meter — rolls up every lesson (checklists +
     quizzes gate lesson completion, so this reflects all three). */}
 {courseProgress && (courseProgress.total_lessons || 0) > 0 && (
 <div
 className="mb-6 p-3 rounded-xl border border-primary/30 bg-primary/5 flex items-center gap-3 flex-wrap"
 data-testid="course-progress-meter"
 >
 <div className="flex-1 min-w-[240px]">
 <div className="flex items-center justify-between text-xs mb-1.5">
 <span className="font-heading font-extrabold uppercase tracking-widest text-primary">
 Course progress · {lesson.course_title || 'Your course'}
 </span>
 <span className="font-semibold text-foreground">
 {Math.round(courseProgress.progress_percentage || 0)}%
 </span>
 </div>
 <ProgressBar
 pct={courseProgress.progress_percentage || 0}
 testid="course-progress-bar"
 />
 <div className="mt-1.5 text-[11px] text-muted-foreground">
 {(courseProgress.completed_lessons || 0)} of {(courseProgress.total_lessons || 0)} lessons complete
 {(courseProgress.progress_percentage || 0) >= 100 && ' · you earned this course badge'}
 </div>
 </div>
 </div>
 )}

 {/* Liability Advisory — amber HomeLyph Pro tone */}
 <div className="mb-6 p-4 bg-primary/5 border border-primary/30 rounded-2xl" data-testid="lesson-disclaimer">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <h3 className="text-sm font-heading font-extrabold text-foreground mb-1">Educational content advisory</h3>
 <p className="text-xs text-foreground/75 leading-relaxed">
 Peak and Prairie Industries, Freeze Dry Academy, and any of its affiliates are not held liable for how this information or these tools are used.
 You assume all responsibility for the proper operation of equipment and food safety practices.
 </p>
 </div>
 </div>
 </div>

 {/* Per-lesson SEO meta — unique title/description/canonical + Course
     LearningResource JSON-LD so each lesson ranks on its own topic. */}
 <PageSEO
   title={lesson.title}
   description={lesson.short_description || lesson.hook || lesson.description}
   path={`/lesson/${lessonId}`}
   type="course"
   schema={buildLessonSchema({ ...lesson, id: lessonId })}
 />

 {/* Refund-window drip notice — only visible during the 14-day window */}
 <RefundWindowBanner state={protectionState} />

 {/* Advertisement - Top Position */}
 {lesson.ad_enabled && lesson.ad_position === "top" && (
 <AdBanner ad={lesson} position="top" />
 )}

 {/* Family B — Headline metric: this lesson at a glance */}
 <div className="mb-6" data-testid="lesson-family-b-metric">
 <HeadlineMetric
 label={lesson.course_title || 'Lesson'}
 value={lesson.title}
 hint={lesson.completed
 ? 'You\u2019ve marked this lesson complete. Feel free to revisit any section below.'
 : 'Read, watch and complete the sections below. Mark as done when you\u2019re ready to move on.'}
 sideMetrics={[
 { label: 'Type', value: (lesson.content_type || 'lesson').toString().replace(/_/g, ' ') },
 { label: 'Duration', value: lesson.duration_minutes ? `${lesson.duration_minutes} min` : '—' },
 { label: 'Status', value: lesson.completed ? 'Done' : 'In progress' },
 ]}
 testId="lesson-headline-metric"
 />
 </div>

 {/* Advertisement Banner - Header */}
 <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-4">
 <div className="flex items-center gap-3">
 <div className="w-11 h-11 bg-primary text-primary-foreground rounded-xl flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-5 h-5" />
 </div>
 <div>
 <p className="text-sm font-heading font-extrabold text-foreground">
 Complete every lesson to earn your course badge
 </p>
 <p className="text-xs text-muted-foreground">
 Track progress and earn professional credentials.
 </p>
 </div>
 </div>
 </div>

 {/* Lesson Header */}
 <Card className="mb-6" data-testid="lesson-header">
 <CardContent className="pt-6">
 <div className="flex items-start justify-between mb-4">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 {lesson.completed ? (
 <CheckCircle className="w-6 h-6 text-green-600" />
 ) : (
 <Circle className="w-6 h-6 text-slate-400" />
 )}
 <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{lesson.title}</h1>
 </div>
 <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-neutral-400">
 {lesson.duration_minutes && (
 <div className="flex items-center gap-2">
 <Clock className="w-4 h-4" />
 <span>{lesson.duration_minutes} minutes</span>
 </div>
 )}
 <div className="flex items-center gap-2">
 <FileText className="w-4 h-4" />
 <span className="capitalize">{lesson.content_type}</span>
 </div>
 </div>
 </div>
 {/* Mark Complete moved to the bottom of the lesson — see the
     "Finish this lesson" card at the end of the page. Header no
     longer duplicates the CTA so learners hit it only once they've
     actually finished reading / watching. */}
 </div>
 </CardContent>
 </Card>

 {/* Ask an Expert — instant AI answer + human fallback */}
 <div className="mb-4 flex items-center gap-2">
 <AskExpertButton
 context={{
 type: 'lesson',
 id: lessonId,
 title: lesson.title,
 snippet: (lesson.description || lesson.summary || '').slice(0, 800),
 }}
 />
 <span className="text-xs text-muted-foreground">
 Stuck on this lesson? Get a straight answer in seconds.
 </span>
 </div>

 {/* Prereq return banner — surfaces when the learner came here from
     the Requirements modal on another lesson. Clicking "Return" jumps
     back manually; marking this lesson complete auto-navigates back. */}
 {returnCtx?.return_to_lesson_id && (
 <div
   className="mb-4 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3 flex items-center gap-3"
   data-testid="prereq-return-banner"
 >
   <div className="h-9 w-9 rounded-full bg-amber-500/20 text-amber-600 flex items-center justify-center flex-shrink-0">
     <ArrowLeft className="w-4 h-4" />
   </div>
   <div className="min-w-0 flex-1">
     <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">
       Catching up on a prerequisite
     </div>
     <div className="text-sm font-semibold truncate">
       Finish this lesson and we'll jump you back to{' '}
       <em>"{returnCtx.return_to_lesson_title || 'your previous lesson'}"</em>.
     </div>
   </div>
   <Button
     size="sm"
     variant="outline"
     className="rounded-full font-bold flex-shrink-0"
     onClick={() => {
       try { localStorage.removeItem('lms:requirements-return'); } catch {}
       const target = returnCtx.return_to_lesson_id;
       setReturnCtx(null);
       navigate(`/lesson/${target}`);
     }}
     data-testid="prereq-return-btn"
   >
     Return now
   </Button>
 </div>
 )}

 {/* Cited sources + audiobook mode + Study panel + Instructor
     availability all live in the right-hand Lesson Tools sidebar
     below. Kept out of the main content flow so learners scroll
     the body without those cards breaking the reading rhythm. */}

 {/* Multi-Content Sections */}
 <ProtectedContent
   lessonId={lessonId}
   courseId={lesson.course_id}
   watermark={protectionState?.watermark}
   testId="lesson-protected-body"
 >
 <div ref={contentRef}>
 {/* Lesson body — this is the AI-generated study content itself
     (or the admin's authored HTML in `description`). Rendered first
     inside the protected wrapper so learners see the actual lesson
     BEFORE the section cards (quiz, key takeaways, diagrams). The
     `description` field is a full HTML block from `_md_to_html`; we
     wrap it in `transformCiteMarkers` so [cite:xyz] tokens turn into
     numbered clickable refs matching the citations panel. */}
 {lesson.description && (
   <Card className="mb-6" data-testid="lesson-body-card">
     <CardContent className="pt-6">
       <AutoTranslatedHtml
         html={transformCiteMarkers(lessonBody.html, lessonCitations)}
         className="prose dark:prose-invert max-w-none prose-headings:font-heading prose-headings:font-extrabold"
         testid="lesson-body-html"
       />
     </CardContent>
   </Card>
 )}
 {lesson.content_sections && lesson.content_sections.length > 0 && (
 <div className="space-y-6 mb-6">
 {lesson.content_sections
 .sort((a, b) => a.order - b.order)
 .map((section, index) => (
 <Card key={section.id || index} id={section.id ? `section-${section.id}` : undefined} className="overflow-hidden scroll-mt-24">
 {section.title && (
 <CardHeader className="bg-pro-soft dark:from-blue-900/20 dark:to-cyan-900/20">
 <CardTitle className="flex items-center gap-2">
 <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
 {index + 1}
 </span>
 {section.title}
 </CardTitle>
 </CardHeader>
 )}
 <CardContent className="pt-6">
 {/* Video Section */}
 {section.type === 'video' && section.video_url && (
 <VideoWatermarkWrap watermark={protectionState?.watermark} className="aspect-video w-full">
 <iframe
 src={`https://www.youtube.com/embed/${
 section.video_url.includes('youtube.com')
 ? new URL(section.video_url).searchParams.get('v')
 : section.video_url.split('/').pop()
 }`}
 className="w-full h-full rounded-lg"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 title={section.title}
 />
 </VideoWatermarkWrap>
 )}

 {/* Rich Text Section */}
 {section.type === 'text' && section.text_content && (
 <AutoTranslatedHtml
 html={transformCiteMarkers(section.text_content, lessonCitations)}
 className="prose dark:prose-invert max-w-none"
 testid={`section-text-${section.id || ''}`}
 />
 )}

 {/* Markdown Section — full markdown → HTML conversion so `##`/`**`
     never leak into the rendered UI. Also runs the result through
     `transformCiteMarkers` so any `[cite:doc-id]` pill still resolves
     to the citations panel. */}
 {section.type === 'markdown' && section.markdown_content && (
 <AutoTranslatedHtml
 html={transformCiteMarkers(mdToHtml(section.markdown_content), lessonCitations)}
 className="prose dark:prose-invert max-w-none prose-headings:font-heading"
 testid={`section-md-${section.id || ''}`}
 />
 )}

 {/* PDF Section */}
 {section.type === 'pdf' && section.pdf_url && (
 <WatermarkedPdfViewer
   pdfUrl={section.pdf_url}
   lessonId={lesson.id}
   title={section.title || lesson.title}
 />
 )}

 {/* Presentation Section */}
 {section.type === 'presentation' && section.presentation_url && (
 <div>
 <div className="aspect-video w-full">
 <iframe
 src={section.presentation_url}
 className="w-full h-full border-2 border-slate-200 dark:border-slate-700 rounded-lg"
 allowFullScreen
 title={section.title}
 />
 </div>
 <p className="text-xs text-slate-500 dark:text-neutral-500 mt-2 text-center">
 💡 Use fullscreen mode for best viewing experience
 </p>
 </div>
 )}

 {/* Quiz Section */}
 {section.type === 'quiz' && section.quiz_data && (
 <QuizSection 
 quizData={section.quiz_data}
 sectionId={section.id}
 sectionTitle={section.title}
 />
 )}

 {/* Checklist Section */}
 {section.type === 'checklist' && (
 <ChecklistSection section={section} lessonId={lesson.id} />
 )}

 {/* Poll Section */}
 {section.type === 'poll' && (
 <PollSection section={section} lessonId={lesson.id} />
 )}

 {/* Image / diagram section — AI-generated Nano Banana diagrams
 land here as `image` type with a base64 payload in `image_url`
 (prefixed `data:image/…`). Render the image and expose the
 prompt as a caption/alt for accessibility. */}
 {section.type === 'image' && (section.image_url || section.markdown_content) && (
 <figure className="flex flex-col items-center gap-2" data-testid={`section-image-${section.id || ''}`}>
 {section.image_url && (
 <img
 src={section.image_url}
 alt={section.image_alt || section.title || 'Lesson diagram'}
 className="max-w-full h-auto rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
 loading="lazy"
 />
 )}
 {(section.image_alt || section.text_content) && (
 <figcaption className="text-xs text-slate-500 dark:text-neutral-400 text-center italic mt-1 max-w-2xl">
 {section.image_alt || section.text_content}
 </figcaption>
 )}
 </figure>
 )}
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 {/* Legacy Single Content (shown only if no content_sections) */}
 {(!lesson.content_sections || lesson.content_sections.length === 0) && (
 <>
 {/* Video Content */}
 {videoId && (
 <Card className="mb-6">
 <CardContent className="pt-6">
 <VideoWatermarkWrap watermark={protectionState?.watermark} className="aspect-video w-full">
 <iframe
 src={`https://www.youtube.com/embed/${videoId}`}
 className="w-full h-full rounded-lg"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 title={lesson.title}
 />
 </VideoWatermarkWrap>
 </CardContent>
 </Card>
 )}

 {/* Written Content */}
 {lesson.written_content && (
 <Card className="mb-6" data-testid="lesson-notes-card">
 <CardHeader>
 <CardTitle>Lesson Notes</CardTitle>
 </CardHeader>
 <CardContent>
 <AutoTranslatedHtml
 html={lesson.written_content}
 className="prose dark:prose-invert max-w-none"
 testid="lesson-notes-body"
 />
 </CardContent>
 </Card>
 )}

 {/* Markdown Content */}
 {lesson.markdown_content && (
 <Card className="mb-6" data-testid="lesson-materials-card">
 <CardHeader>
 <CardTitle>Course Materials</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="prose dark:prose-invert max-w-none whitespace-pre-wrap">
 <AutoTranslated as="span" testid="lesson-materials-body">
 {lesson.markdown_content}
 </AutoTranslated>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Advertisement - Middle Position */}
 {lesson.ad_enabled && lesson.ad_position === "middle" && (
 <AdBanner ad={lesson} position="middle" />
 )}

 {/* PDF Content */}
 {lesson.pdf_url && (
 <Card className="mb-6">
 <CardHeader>
 <CardTitle>Downloadable Materials</CardTitle>
 </CardHeader>
 <CardContent>
 <Button
 onClick={() => window.open(lesson.pdf_url, '_blank')}
 variant="outline"
 className="w-full"
 >
 <FileText className="w-4 h-4 mr-2" />
 Download PDF
 </Button>
 </CardContent>
 </Card>
 )}
 </>
 )}
 </div>
 </ProtectedContent>

 {/* Quiz Section */}
 {lesson.quiz && (
 <Card className="mb-6">
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle>Lesson Quiz</CardTitle>
 {lesson.quiz_attempt && (
 <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
 lesson.quiz_attempt.passed 
 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
 : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
 }`}>
 Last Score: {lesson.quiz_attempt.score}%
 </span>
 )}
 </div>
 </CardHeader>
 <CardContent>
 {!showQuiz && !quizResult ? (
 <div className="text-center py-8">
 <p className="text-slate-600 dark:text-neutral-400 mb-4">
 Test your knowledge with {lesson.quiz.questions.length} questions
 </p>
 <p className="text-sm text-slate-500 dark:text-neutral-500 mb-6">
 Passing score: {lesson.quiz.passing_score}%
 </p>
 <Button onClick={() => setShowQuiz(true)} className="bg-amber-600 hover:bg-amber-700">
 Start Quiz
 </Button>
 </div>
 ) : quizResult ? (
 <div className="text-center py-8">
 <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
 quizResult.passed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
 }`}>
 <span className={`text-3xl font-bold ${
 quizResult.passed ? 'text-green-600' : 'text-red-600'
 }`}>
 {quizResult.score}%
 </span>
 </div>
 <h3 className={`text-2xl font-bold mb-2 ${
 quizResult.passed ? 'text-green-600' : 'text-red-600'
 }`}>
 {quizResult.passed ? '🎉 Quiz Passed!' : '📚 Keep Learning'}
 </h3>
 <p className="text-slate-600 dark:text-neutral-400 mb-6">
 You got {quizResult.correct_count} out of {quizResult.total_questions} questions correct
 </p>
 <div className="flex gap-3 justify-center">
 {!quizResult.passed && (
 <Button 
 onClick={() => {
 setShowQuiz(true);
 setQuizResult(null);
 setQuizAnswers({});
 }}
 className="bg-primary text-primary-foreground hover:bg-primary/90"
 >
 Retry Quiz
 </Button>
 )}
 <Button 
 onClick={() => navigate(-1)}
 variant="outline"
 >
 Back to Course
 </Button>
 </div>
 </div>
 ) : (
 <div className="space-y-6">
 {lesson.quiz.questions.map((question, index) => (
 <div key={index} className="p-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg">
 <p className="font-semibold text-slate-900 dark:text-white mb-3">
 {index + 1}. {question.question}
 </p>
 
 {question.type === 'multiple_choice' && question.options && (
 <div className="space-y-2">
 {question.options.map((option, optIndex) => (
 <label
 key={optIndex}
 className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20"
 >
 <input
 type="radio"
 name={`question-${index}`}
 value={option}
 checked={quizAnswers[index] === option}
 onChange={(e) => setQuizAnswers(prev => ({ ...prev, [index]: e.target.value }))}
 className="w-4 h-4"
 />
 <span className="text-slate-700 dark:text-neutral-300">{option}</span>
 </label>
 ))}
 </div>
 )}
 
 {question.type === 'true_false' && (
 <div className="space-y-2">
 {['True', 'False'].map((option) => (
 <label
 key={option}
 className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20"
 >
 <input
 type="radio"
 name={`question-${index}`}
 value={option}
 checked={quizAnswers[index] === option}
 onChange={(e) => setQuizAnswers(prev => ({ ...prev, [index]: e.target.value }))}
 className="w-4 h-4"
 />
 <span className="text-slate-700 dark:text-neutral-300">{option}</span>
 </label>
 ))}
 </div>
 )}
 
 {question.type === 'short_answer' && (
 <input
 type="text"
 value={quizAnswers[index] || ''}
 onChange={(e) => setQuizAnswers(prev => ({ ...prev, [index]: e.target.value }))}
 className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-lg"
 placeholder="Type your answer..."
 />
 )}
 </div>
 ))}
 
 <div className="flex gap-3">
 <Button
 onClick={submitQuiz}
 className="flex-1 bg-amber-600 hover:bg-amber-700"
 disabled={Object.keys(quizAnswers).length !== lesson.quiz.questions.length}
 >
 Submit Quiz
 </Button>
 <Button
 onClick={() => {
 setShowQuiz(false);
 setQuizAnswers({});
 }}
 variant="outline"
 >
 Cancel
 </Button>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 )}

 {/* Support Materials Section */}
 {(lesson.support_photos?.length > 0 || lesson.support_videos?.length > 0 || 
 lesson.support_links?.length > 0 || lesson.support_downloads?.length > 0) && (
 <Card className="mb-6">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Sparkles className="w-5 h-5" />
 Support Materials
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 {/* Photos Gallery */}
 {lesson.support_photos?.length > 0 && (
 <div>
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 <Image className="w-4 h-4" />
 Reference Photos
 </h4>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
 {lesson.support_photos.map((photoUrl, index) => (
 <a
 key={index}
 href={photoUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="block aspect-square rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700 hover:border-blue-500 transition-colors"
 >
 <WatermarkedImage
 src={photoUrl}
 alt={`Reference ${index + 1}`}
 lessonId={lesson.id}
 className="w-full h-full object-cover hover:scale-105 transition-transform"
 onError={(e) => {
 e.target.src = 'https://via.placeholder.com/150?text=Image';
 }}
 />
 </a>
 ))}
 </div>
 </div>
 )}

 {/* Additional Videos */}
 {lesson.support_videos?.length > 0 && (
 <div>
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 <Video className="w-4 h-4" />
 Additional Videos
 </h4>
 <div className="grid md:grid-cols-2 gap-4">
 {lesson.support_videos.map((videoUrl, index) => {
 const videoId = videoUrl.includes('youtube.com') 
 ? new URL(videoUrl).searchParams.get('v')
 : videoUrl.split('/').pop();
 return (
 <div key={index} className="aspect-video rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-700">
 <VideoWatermarkWrap watermark={protectionState?.watermark} className="w-full h-full">
 <iframe
 src={`https://www.youtube.com/embed/${videoId}`}
 title={`Support Video ${index + 1}`}
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 className="w-full h-full"
 />
 </VideoWatermarkWrap>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Related Links */}
 {lesson.support_links?.length > 0 && (
 <div>
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 <ExternalLink className="w-4 h-4" />
 Related Links
 </h4>
 <div className="space-y-2">
 {lesson.support_links.map((link, index) => (
 <a
 key={index}
 href={link.url}
 target="_blank"
 rel="noopener noreferrer"
 className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
 >
 <div className="flex items-center justify-between">
 <span className="font-medium text-slate-900 dark:text-white group-hover:text-primary dark:group-hover:text-blue-400">
 {link.title}
 </span>
 <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-primary" />
 </div>
 <span className="text-xs text-slate-500 dark:text-neutral-500 truncate block mt-1">
 {link.url}
 </span>
 </a>
 ))}
 </div>
 </div>
 )}

 {/* Downloadable Files */}
 {lesson.support_downloads?.length > 0 && (
 <div>
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 <Download className="w-4 h-4" />
 Downloadable Resources
 </h4>
 <div className="space-y-2">
 {lesson.support_downloads.map((download, index) => (
 <a
 key={index}
 href={download.url}
 download
 className="block p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-700 hover:border-green-500 hover:shadow-md transition-all group"
 >
 <div className="flex items-center justify-between">
 <span className="font-medium text-slate-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
 {download.title}
 </span>
 <Download className="w-4 h-4 text-green-600 group-hover:scale-110 transition-transform" />
 </div>
 <span className="text-xs text-slate-500 dark:text-neutral-500 truncate block mt-1">
 {download.url}
 </span>
 </a>
 ))}
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 )}

 {/* Advertisement - Bottom Position */}
 {lesson.ad_enabled && lesson.ad_position === "bottom" && (
 <AdBanner ad={lesson} position="bottom" />
 )}

 {/* Global lesson_below placement — image-based sponsor slot */}
 <div className="mt-4">
 <AdSlot location="lesson_below" variant="footer" />
 </div>

 {/* Assignments for this lesson */}
 <AssignmentPanel lessonId={lesson.id} />

 {/* Threaded lesson discussion */}
 <DiscussionPanel lessonId={lesson.id} />

{/* Related on this topic — cross-links to sibling courses + articles
    on the same topic hub. Uses lesson + course title as hints. */}
<RelatedOnTopic
  category={lesson.category || lesson.course_category}
  themes={[
    ...(lesson.themes || []),
    ...(lesson.skill_tags || []),
    ...(lesson.course_themes || []),
    ...(lesson.course_skill_tags || []),
  ]}
  title={`${lesson.course_title || ''} ${lesson.title || ''}`.trim()}
  excludeCourseId={lesson.course_id}
  excludeLessonId={lesson.id}
/>


 {/* Lesson Navigation */}
 <div className="mt-8 space-y-4">
 {/* Navigation Buttons */}
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center justify-between gap-4">
 {/* Previous Lesson Button */}
 {lesson.prev_lesson ? (
 <Button
 variant="outline"
 onClick={() => navigate(`/lesson/${lesson.prev_lesson.id}`)}
 className="flex-1"
 >
 <ChevronLeft className="w-4 h-4 mr-2" />
 <div className="text-left">
 <div className="text-xs text-slate-500">Previous</div>
 <div className="font-medium truncate">{lesson.prev_lesson.title}</div>
 </div>
 </Button>
 ) : (
 <Button
 variant="outline"
 onClick={() => lesson.course_id ? navigate(`/course/${lesson.course_id}`) : navigate(-1)}
 className="flex-1"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Course
 </Button>
 )}

 {/* Next Lesson Button */}
 {lesson.next_lesson ? (
 <Button
 onClick={() => navigate(`/lesson/${lesson.next_lesson.id}`)}
 className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
 >
 <div className="text-right">
 <div className="text-xs opacity-90">Next</div>
 <div className="font-medium truncate">{lesson.next_lesson.title}</div>
 </div>
 <ChevronRight className="w-4 h-4 ml-2" />
 </Button>
 ) : (
 <Button
 onClick={() => lesson.course_id ? navigate(`/course/${lesson.course_id}`) : navigate(-1)}
 className="flex-1 bg-green-600 hover:bg-green-700"
 >
 <CheckCircle className="w-4 h-4 mr-2" />
 Course Complete!
 </Button>
 )}
 </div>
 </CardContent>
 </Card>

 {/* Keyboard Shortcuts Hint */}
 <p className="text-xs text-center text-slate-500 dark:text-neutral-500">
 💡 Tip: Use ← → arrow keys to navigate between lessons
 </p>
 </div>

 {/* Completion Modal */}
 {showCompletionModal && lesson.next_lesson && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <Card className="max-w-md w-full animate-in fade-in zoom-in duration-300">
 <CardHeader className="text-center pb-4">
 <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
 <CheckCircle className="w-10 h-10 text-green-600" />
 </div>
 <CardTitle className="text-2xl">Lesson Complete!</CardTitle>
 <CardDescription>
 Great job! You&apos;ve completed this lesson.
 </CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
 <p className="text-sm font-semibold text-slate-700 dark:text-neutral-300 mb-2">
 Up Next:
 </p>
 <p className="text-slate-900 dark:text-white font-medium">
 {lesson.next_lesson.title}
 </p>
 </div>
 <div className="flex gap-3">
 <Button
 variant="outline"
 onClick={() => {
 setShowCompletionModal(false);
 if (lesson.course_id) navigate(`/course/${lesson.course_id}`);
 else navigate(-1);
 }}
 className="flex-1"
 >
 Back to Course
 </Button>
 <Button
 onClick={() => {
 setShowCompletionModal(false);
 navigate(`/lesson/${lesson.next_lesson.id}`);
 }}
 className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
 >
 Next Lesson
 <ChevronRight className="w-4 h-4 ml-2" />
 </Button>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Course-level Certificate Celebration — fires when the LAST lesson
     tips the whole course into complete. Uses a global overlay so it
     stacks above the per-lesson modal above. */}
 {certificate && (
 <CourseCompletionCelebration
 certificate={certificate}
 onClose={() => setCertificate(null)}
 />
 )}

{/* Milestone toast — celebrates course progress at 25/50/75% */}
<MilestoneToast
milestone={milestone}
courseTitle={lesson?.course_title || 'this course'}
courseId={lesson?.course_id}
holderName={user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : ''}
onClose={() => setMilestone(null)}
/>

{/* Module Complete celebration — fires when the user just cleared the
    final remaining lesson of the current module. */}
<ModuleCompleteModal
open={!!moduleCompletion}
moduleId={moduleCompletion?.module_id}
moduleTitle={moduleCompletion?.module_title}
courseTitle={moduleCompletion?.course_title}
holderName={user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : ''}
streakDays={moduleCompletion?.streak || 0}
apiBase={process.env.REACT_APP_BACKEND_URL || ''}
onContinue={() => {
setModuleCompletion(null);
if (lesson?.next_lesson) navigate(`/lesson/${lesson.next_lesson.id}`);
else if (lesson?.course_id) navigate(`/course/${lesson.course_id}`);
}}
onClose={() => setModuleCompletion(null)}
continueLabel={lesson?.next_lesson ? 'Next lesson' : 'Back to course'}
/>

{/* Confidence survey — fires after lesson complete and after quiz fail. */}
<ConfidenceSurvey
  open={confidenceState.open}
  trigger={confidenceState.trigger}
  onClose={() => setConfidenceState((s) => ({ ...s, open: false }))}
  API={API}
  context={{
    lesson_id: lesson?.id,
    course_id: lesson?.course_id,
    lesson_title: lesson?.title,
    course_title: lesson?.course_title,
    quiz_id: confidenceState.trigger === 'quiz_failed' ? lesson?.quiz?.id : undefined,
  }}
/>

 {/* Finish this lesson — Mark Complete lives at the bottom so
     learners hit the CTA only after actually reading the body,
     watching diagrams, and reviewing the citations panel. */}
 <Card className="mt-8 mb-6 border-primary/30" data-testid="lesson-finish-card">
 <CardContent className="pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
 <div className="flex-1">
 <p className="text-sm text-slate-600 dark:text-neutral-400">
 {lesson.completed
   ? 'Nice — this lesson is marked complete. You can mark it incomplete if you want to revisit it.'
   : 'Done reading? Mark this lesson complete to unlock the next one and record your progress.'}
 </p>
 </div>
 <Button
 onClick={toggleComplete}
 disabled={completing}
 size="lg"
 className={lesson.completed ? 'bg-green-600 hover:bg-green-700 min-w-[180px]' : 'bg-primary text-primary-foreground hover:bg-primary/90 min-w-[180px]'}
 data-testid="lesson-mark-complete-btn"
 >
 {lesson.completed ? (
 <>
 <CheckCircle className="w-4 h-4 mr-2" />
 Completed
 </>
 ) : (
 <>
 <Circle className="w-4 h-4 mr-2" />
 Mark Complete
 </>
 )}
 </Button>
 </CardContent>
 </Card>

 {/* end of main content column */}
 </div>

 {/* Lesson Tools — right-hand sidebar (sticky on desktop, stacks
     below content on mobile). Groups every non-body surface the
     learner needs while working through the lesson: audiobook
     launcher, cited sources, instructor availability, peer study
     rooms, quick nav shortcuts, and (when the admin has enabled
     ads with position sidebar) a long-banner ad slot. */}
 <aside
   className="mt-8 lg:mt-0 w-full lg:w-[340px] lg:shrink-0 lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1 space-y-4"
   data-testid="lesson-sidebar"
 >
   {/* Table of Contents — auto-generated from the body's <h2> tags so
       long lessons don't force endless scrolling. Only rendered when
       the body has 2+ headings; a single-heading lesson doesn't
       benefit from a jump list. */}
   {lessonBody.toc.length >= 2 && (
     <Card data-testid="lesson-toc-card">
       <CardHeader className="pb-3">
         <CardTitle className="text-sm font-heading font-extrabold uppercase tracking-widest text-primary flex items-center gap-2">
           <FileText className="w-4 h-4" />
           On this page
           <span
             className="ml-auto text-[10px] font-bold text-foreground/60 normal-case tracking-normal"
             data-testid="lesson-toc-progress"
           >
             {sectionsRead.size}/{lessonBody.toc.length} read
           </span>
         </CardTitle>
       </CardHeader>
       <CardContent className="pt-0">
         <ol className="space-y-1 text-sm list-none pl-0">
           {lessonBody.toc.map((h, i) => {
             const isRead = sectionsRead.has(h.id);
             return (
               <li key={h.id} className="flex items-start gap-2">
                 <span
                   className={`text-[11px] font-bold mt-0.5 w-4 shrink-0 text-right transition-colors ${
                     isRead ? 'text-green-600' : 'text-primary/70'
                   }`}
                   data-testid={`lesson-toc-num-${h.id}`}
                 >
                   {isRead ? '✓' : `${i + 1}.`}
                 </span>
                 <button
                   type="button"
                   onClick={() => {
                     document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                   }}
                   className={`text-left hover:text-primary hover:underline underline-offset-2 line-clamp-2 transition-colors ${
                     isRead ? 'text-green-700/85 dark:text-green-400/85' : 'text-foreground/85'
                   }`}
                   data-testid={`lesson-toc-link-${h.id}`}
                 >
                   {h.text}
                 </button>
               </li>
             );
           })}
         </ol>
       </CardContent>
     </Card>
   )}

   {/* Content section nav — surfaces diagrams / quizzes / checklists /
       polls / videos / PDFs as jump links so learners can hop straight
       to any figure or activity. Section anchor ids match the `id`
       on each section <Card>. */}
   {(lesson.content_sections?.length || 0) > 0 && (
     <Card data-testid="lesson-sections-nav-card">
       <CardHeader className="pb-3">
         <CardTitle className="text-sm font-heading font-extrabold uppercase tracking-widest text-primary flex items-center gap-2">
           <Sparkles className="w-4 h-4" />
           In this lesson
         </CardTitle>
       </CardHeader>
       <CardContent className="pt-0">
         <ul className="space-y-1 text-sm list-none pl-0">
           {[...(lesson.content_sections || [])]
             .sort((a, b) => (a.order || 0) - (b.order || 0))
             .filter((s) => !!s.id)
             .map((s) => {
               const glyph = ({
                 video:      '▶',
                 image:      '🖼',
                 quiz:       '?',
                 checklist:  '☑',
                 poll:       '❓',
                 pdf:        '📄',
                 presentation: '🎞',
                 markdown:   '§',
                 text:       '§',
               })[s.type] || '§';
               const label = s.title || (
                 s.type === 'image' ? 'Diagram'
                 : s.type === 'quiz' ? 'Quick check'
                 : s.type.charAt(0).toUpperCase() + s.type.slice(1)
               );
               return (
                 <li key={s.id} className="flex items-start gap-2">
                   <span
                     className="text-xs mt-0.5 w-5 shrink-0 text-center text-primary/70"
                     aria-hidden="true"
                   >
                     {glyph}
                   </span>
                   <button
                     type="button"
                     onClick={() => {
                       document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                     }}
                     className="text-left text-foreground/85 hover:text-primary hover:underline underline-offset-2 line-clamp-2"
                     data-testid={`lesson-section-nav-${s.id}`}
                   >
                     {label}
                     <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                       {s.type}
                     </span>
                   </button>
                 </li>
               );
             })}
         </ul>
       </CardContent>
     </Card>
   )}

   <Card data-testid="lesson-tools-card">
     <CardHeader className="pb-3">
       <CardTitle className="text-sm font-heading font-extrabold uppercase tracking-widest text-primary flex items-center gap-2">
         <Sparkles className="w-4 h-4" />
         Lesson tools
       </CardTitle>
     </CardHeader>
     <CardContent className="pt-0 space-y-1.5">
       <Button
         variant="ghost"
         size="sm"
         onClick={() => setAudiobookOpen(true)}
         className="w-full justify-start"
         data-testid="lesson-tools-audiobook"
       >
         <Headphones className="w-4 h-4 mr-2" />
         Listen (audiobook)
       </Button>
       <Button
         variant="ghost"
         size="sm"
         onClick={() => {
           if (instructorId) navigate(`/instructor/${instructorId}`);
           else toast.info('Instructor profile not available for this lesson yet.');
         }}
         className="w-full justify-start"
         data-testid="lesson-tools-instructor"
       >
         <BookOpen className="w-4 h-4 mr-2" />
         Instructor &amp; office hours
       </Button>
       <Button
         variant="ghost"
         size="sm"
         onClick={() => setStudyModalOpen(true)}
         className="w-full justify-start"
         data-testid="lesson-tools-study"
       >
         <BookOpen className="w-4 h-4 mr-2" />
         Study together
       </Button>
       <Button
         variant="ghost"
         size="sm"
         onClick={() => setRequirementsOpen(true)}
         className="w-full justify-start"
         data-testid="lesson-tools-requirements"
       >
         <FileText className="w-4 h-4 mr-2" />
         Lesson requirements
       </Button>
       {/* Inline unit converter — pressure (mTorr/Torr/atm/psi/kPa/bar)
           + temperature (°C/°F/K). Popover so it stays inside the
           lesson viewer instead of opening a new page. */}
       <UnitConverterPopover />
     </CardContent>
   </Card>

   <div data-testid="lesson-sidebar-citations">
     <LessonCitationsPanel
       lessonId={lessonId}
       citations={lessonCitations.length ? lessonCitations : undefined}
     />
   </div>

   <div data-testid="lesson-sidebar-study">
     <LessonStudyPanel lessonId={lessonId} lessonTitle={lesson.title} />
   </div>

   {lesson.ad_enabled && (lesson.ad_position === 'sidebar' || lesson.ad_position === 'side') && (
     <div data-testid="lesson-sidebar-ad">
       <AdBanner ad={lesson} position="sidebar" />
     </div>
   )}
 </aside>

 {/* end of two-column layout */}
 </div>

 {/* Audiobook mode overlay — mounted at the LessonViewer root so it
     sits above every other lesson-page surface. Fed the fully-flattened
     plain-text of the lesson for TTS synthesis. */}
 <LessonAudiobookMode
   open={audiobookOpen}
   onClose={() => setAudiobookOpen(false)}
   lessonId={lessonId}
   lessonTitle={lesson?.title || 'Lesson'}
   lessonText={buildLessonPlainText(lesson)}
 />

 <LessonExtraModals
   studyOpen={studyModalOpen}
   onCloseStudy={() => setStudyModalOpen(false)}
   requirementsOpen={requirementsOpen}
   onCloseRequirements={() => setRequirementsOpen(false)}
   lessonId={lessonId}
   lessonTitle={lesson?.title || 'Lesson'}
   courseId={lesson?.course_id || lesson?.courseId || null}
   lesson={lesson}
 />


 </div>
 </div>
 );
};

// Quiz Section Component
const QuizSection = ({ quizData, sectionId, sectionTitle }) => {
 const [quizStarted, setQuizStarted] = useState(false);
 const [userAnswers, setUserAnswers] = useState({});
 const [showResults, setShowResults] = useState(false);
 const [score, setScore] = useState(0);

 const handleAnswerChange = (questionIndex, answer) => {
 setUserAnswers({ ...userAnswers, [questionIndex]: answer });
 };

 const submitQuiz = () => {
 let correctCount = 0;
 quizData.questions.forEach((q, index) => {
 if (userAnswers[index] === q.correct_answer) {
 correctCount++;
 }
 });
 
 const percentage = (correctCount / quizData.questions.length) * 100;
 setScore(percentage);
 setShowResults(true);
 };

 const resetQuiz = () => {
 setUserAnswers({});
 setShowResults(false);
 setScore(0);
 setQuizStarted(false);
 };

 if (!quizStarted) {
 return (
 <div className="text-center py-8">
 <div className="inline-block p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
 <h3 className="text-xl font-bold mb-2">{sectionTitle || 'Quiz'}</h3>
 <p className="text-slate-600 dark:text-neutral-400 mb-4">
 {quizData.questions.length} questions • Passing score: {quizData.passing_score}%
 </p>
 <Button onClick={() => setQuizStarted(true)} className="bg-primary text-primary-foreground hover:bg-primary/90">
 Start Quiz
 </Button>
 </div>
 </div>
 );
 }

 if (showResults) {
 const passed = score >= quizData.passing_score;
 return (
 <div className={`p-6 rounded-lg ${passed ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500' : 'bg-red-50 dark:bg-red-900/20 border-2 border-red-500'}`}>
 <h3 className="text-2xl font-bold mb-4">
 {passed ? '✅ Quiz Passed!' : '❌ Quiz Not Passed'}
 </h3>
 <p className="text-xl mb-4">Your Score: {score.toFixed(0)}%</p>
 <p className="mb-6">
 You answered {Math.round((score / 100) * quizData.questions.length)} out of {quizData.questions.length} questions correctly.
 </p>

 {quizData.show_feedback && (
 <div className="space-y-4 mb-6">
 <h4 className="font-semibold">Review Your Answers:</h4>
 {quizData.questions.map((q, index) => {
 const userAnswer = userAnswers[index];
 const isCorrect = userAnswer === q.correct_answer;
 
 return (
 <div key={index} className={`p-4 rounded border-2 ${isCorrect ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : 'border-red-500 bg-red-50 dark:bg-red-900/10'}`}>
 <p className="font-medium mb-2">
 {index + 1}. {q.question}
 </p>
 <p className="text-sm">
 <strong>Your answer:</strong> {userAnswer || '(Not answered)'}
 </p>
 {!isCorrect && (
 <p className="text-sm text-green-700 dark:text-green-300">
 <strong>Correct answer:</strong> {q.correct_answer}
 </p>
 )}
 {q.explanation && (
 <p className="text-sm mt-2 text-slate-600 dark:text-neutral-400">
 <strong>Explanation:</strong> {q.explanation}
 </p>
 )}
 </div>
 );
 })}
 </div>
 )}

 <Button onClick={resetQuiz} variant="outline">
 Retake Quiz
 </Button>
 </div>
 );
 }

 return (
 <div className="space-y-6">
 {quizData.questions.map((question, index) => (
 <div key={index} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
 <p className="font-semibold mb-3">
 {index + 1}. {question.question}
 </p>

 {question.type === 'multiple_choice' && (
 <div className="space-y-2">
 {question.options?.filter(opt => opt.trim()).map((option, optIndex) => (
 <label key={optIndex} className="flex items-center gap-2 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer">
 <input
 type="radio"
 name={`question-${index}`}
 value={option}
 checked={userAnswers[index] === option}
 onChange={() => handleAnswerChange(index, option)}
 className="w-4 h-4"
 />
 <span>{option}</span>
 </label>
 ))}
 </div>
 )}

 {question.type === 'true_false' && (
 <div className="space-y-2">
 <label className="flex items-center gap-2 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer">
 <input
 type="radio"
 name={`question-${index}`}
 value="True"
 checked={userAnswers[index] === 'True'}
 onChange={() => handleAnswerChange(index, 'True')}
 className="w-4 h-4"
 />
 <span>True</span>
 </label>
 <label className="flex items-center gap-2 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer">
 <input
 type="radio"
 name={`question-${index}`}
 value="False"
 checked={userAnswers[index] === 'False'}
 onChange={() => handleAnswerChange(index, 'False')}
 className="w-4 h-4"
 />
 <span>False</span>
 </label>
 </div>
 )}

 {question.type === 'short_answer' && (
 <Input
 value={userAnswers[index] || ''}
 onChange={(e) => handleAnswerChange(index, e.target.value)}
 placeholder="Type your answer here..."
 className="mt-2"
 />
 )}
 </div>
 ))}

 <Button 
 onClick={submitQuiz}
 className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
 disabled={Object.keys(userAnswers).length !== quizData.questions.length}
 >
 Submit Quiz
 </Button>
 
 {Object.keys(userAnswers).length !== quizData.questions.length && (
 <p className="text-sm text-slate-500 text-center">
 Please answer all questions to submit
 </p>
 )}
 </div>
 );
};

export default LessonViewer;
